import React, { useEffect, useState, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { getUserProfile, resetUserStatus } from '../utils/api';
import io from 'socket.io-client';
import Swal from 'sweetalert2';
import './WaitingApproval.css';

const SOCKET_URL = process.env.REACT_APP_SOCKET_URL || 'http://localhost:5000';

function WaitingApproval() {
    const { user, logout, updateUser } = useAuth();
    const navigate = useNavigate();
    const [socket, setSocket] = useState(null);
    const [checkingStatus, setCheckingStatus] = useState(false);
    const pollIntervalRef = useRef(null);
    const isExitingRef = useRef(false);
    
    // Support both _id and id fields (MongoDB returns _id, but some places use id)
    const getUserId = (userObj) => userObj?.id || userObj?._id;
    const userIdRef = useRef(getUserId(user));

    // Update ref when user.id or user._id changes
    useEffect(() => {
        userIdRef.current = getUserId(user);
    }, [user]);

    // Initialize userProfile as null - we'll fetch from API immediately
    const [userProfile, setUserProfile] = useState(null);

    // Check user status periodically - this is the source of truth
    const checkUserStatus = async () => {
        if (isExitingRef.current) {
            return;
        }
        const currentUserId = userIdRef.current;
        console.log('🔍 checkUserStatus called, userId:', currentUserId, 'checkingStatus:', checkingStatus);
        if (!currentUserId) {
            console.warn('⚠️ No userId in ref, aborting check');
            return;
        }
        if (checkingStatus) {
            console.log('⏳ Already checking status, skipping...');
            return;
        }
        
        console.log('📡 Starting API call to fetch user profile...');
        setCheckingStatus(true);
        try {
            const profile = await getUserProfile(currentUserId);
            console.log('🔍 Raw API response:', profile);
            if (isExitingRef.current) {
                return;
            }
            if (profile) {
                console.log('📋 User profile loaded:', {
                    status: profile.status,
                    rejectionReason: profile.rejectionReason,
                    isApproved: profile.isApproved,
                    _id: profile._id,
                    nickname: profile.nickname
                });
                
                // Update userProfile state FIRST (this is what the UI reads from)
                setUserProfile(profile);
                
                // THEN update user in context and localStorage to keep them in sync
                updateUser(profile);
                
                // Log for debugging
                console.log('✅ State updated - userProfile.status =', profile.status);
                
                // If approved, redirect to dashboard
                if (profile.isApproved && profile.status === 'approved') {
                    console.log('✅ User approved! Redirecting to dashboard...');
                    navigate('/female-dashboard');
                    return;
                }
            }
        } catch (error) {
            console.error('❌ Error checking user status:', error);
            console.error('Error details:', error.response?.data || error.message);
        } finally {
            setCheckingStatus(false);
            console.log('✅ Status check completed');
        }
    };

    useEffect(() => {
        const currentUserId = getUserId(user);
        console.log('🚀 WaitingApproval mounted, user:', user);
        console.log('🔍 Extracted userId:', currentUserId);
        if (!currentUserId) {
            console.warn('⚠️ No user.id or user._id found, cannot check status');
            return;
        }

        console.log('✅ User ID found:', currentUserId, 'Starting status check...');
        // Initial check - fetch fresh data immediately on mount
        // Don't rely on localStorage or context - get it from API
        checkUserStatus();

        // Set up Socket.IO connection
        const newSocket = io(SOCKET_URL);
        setSocket(newSocket);

        // Listen for approval notification
        newSocket.on(`user-approved-${currentUserId}`, (data) => {
            if (isExitingRef.current) return;
            console.log('✅ Received approval notification:', data);
            // Update user and redirect
            updateUser({ ...user, isApproved: true, status: 'approved' });
            navigate('/female-dashboard');
        });

        // Listen for rejection notification
        newSocket.on(`user-rejected-${currentUserId}`, async (data) => {
            if (isExitingRef.current) return;
            console.log('❌ Received rejection notification:', data);
            // Immediately update user profile to show rejection reason
            try {
                const profile = await getUserProfile(currentUserId);
                if (isExitingRef.current) return;
                if (profile) {
                    console.log('📋 Updated profile from rejection notification:', {
                        status: profile.status,
                        rejectionReason: profile.rejectionReason
                    });
                    updateUser(profile);
                    setUserProfile(profile);
                }
            } catch (error) {
                console.error('Error loading profile after rejection:', error);
            }
            // Also check status in case API didn't respond immediately
            setTimeout(() => checkUserStatus(), 1000);
        });

        // Poll status every 5 seconds
        const interval = setInterval(() => {
            checkUserStatus();
        }, 5000);
        pollIntervalRef.current = interval;

        return () => {
            clearInterval(interval);
            pollIntervalRef.current = null;
            if (newSocket && currentUserId) {
                newSocket.off(`user-approved-${currentUserId}`);
                newSocket.off(`user-rejected-${currentUserId}`);
                newSocket.disconnect();
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user]);

    const handleLogout = () => {
        isExitingRef.current = true;
        if (pollIntervalRef.current) {
            clearInterval(pollIntervalRef.current);
            pollIntervalRef.current = null;
        }
        if (socket) {
            socket.disconnect();
        }
        logout();
        navigate('/', { replace: true });
    };

    const handleReapply = async () => {
        // Show warning dialog using SweetAlert2 before allowing reapplication
        const result = await Swal.fire({
            icon: 'warning',
            title: '⚠️ Warning',
            html: '<p style="text-align: left; margin: 15px 0;">Please make sure you have addressed the issues mentioned in the rejection reason before reapplying.</p><p style="text-align: left;">If you have fixed the problems and would like to reapply, click "Continue" to proceed.</p>',
            showCancelButton: true,
            confirmButtonText: 'Continue',
            cancelButtonText: 'Cancel',
            confirmButtonColor: '#4CAF50',
            cancelButtonColor: '#999',
            reverseButtons: true
        });

        if (result.isConfirmed) {
            const currentUserId = getUserId(user);
            if (!currentUserId) {
                Swal.fire('Error', 'User ID not found', 'error');
                return;
            }

            try {
                // Show loading
                Swal.fire({
                    title: 'Processing...',
                    text: 'Resetting your status for reapplication',
                    allowOutsideClick: false,
                    didOpen: () => {
                        Swal.showLoading();
                    }
                });

                // Reset user status to pending
                await resetUserStatus(currentUserId);

                // Close socket
                if (socket) {
                    socket.disconnect();
                }

                // Update user context
                const updatedUser = {
                    ...user,
                    status: 'pending',
                    isApproved: false,
                    rejectionReason: null
                };
                updateUser(updatedUser);

                // Set gender in localStorage
                if (user?.gender) {
                    localStorage.setItem('selectedGender', user.gender);
                }

                // Redirect to profile edit page
                Swal.close();
                // Redirect to profile edit page (reapply is for updating existing account)
                navigate('/edit-profile');
            } catch (error) {
                console.error('Error resetting user status:', error);
                Swal.fire('Error', 'Failed to reset status. Please try again.', 'error');
            }
        }
    };

    return (
        <div className="waiting-page">
            <div className="waiting-container">
                <div className="waiting-card">
                    <div className="icon-container">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <circle cx="12" cy="12" r="10"/>
                            <polyline points="12 6 12 12 16 14"/>
                        </svg>
                    </div>
                    <h1>{userProfile?.status === 'rejected' ? 'Registration Status' : 'Pending Approval'}</h1>
                    <p className="subtitle">
                        Thank you for registering, {user?.nickname}!
                    </p>
                    {/* Debug: Show status info */}
                    {(process.env.NODE_ENV === 'development') && (
                        <div style={{ fontSize: '12px', color: '#999', marginBottom: '10px', textAlign: 'left', padding: '10px', background: '#f0f0f0', borderRadius: '5px' }}>
                            <strong>Debug:</strong><br/>
                            userProfile.status = {userProfile?.status || 'null'}<br/>
                            user.status = {user?.status || 'null'}<br/>
                            userProfile.rejectionReason = {userProfile?.rejectionReason || 'null'}<br/>
                            user.rejectionReason = {user?.rejectionReason || 'null'}
                        </div>
                    )}
                    {userProfile?.status === 'rejected' ? (
                        <div className="rejection-box" style={{
                            background: '#fee',
                            border: '2px solid #fcc',
                            borderRadius: '10px',
                            padding: '30px',
                            marginBottom: '30px',
                            textAlign: 'left'
                        }}>
                            <h2 style={{ color: '#c33', marginBottom: '15px', fontSize: '24px' }}>❌ Registration Rejected</h2>
                            <p style={{ color: '#666', marginBottom: '15px', fontSize: '16px' }}>
                                Unfortunately, your registration has been rejected by our admin team.
                            </p>
                            {userProfile?.rejectionReason && (
                                <div style={{
                                    background: 'white',
                                    padding: '15px',
                                    borderRadius: '8px',
                                    marginTop: '15px',
                                    border: '1px solid #ddd'
                                }}>
                                    <strong style={{ color: '#333', display: 'block', marginBottom: '10px' }}>Reason for rejection:</strong>
                                    <p style={{ color: '#666', margin: 0, lineHeight: '1.6' }}>
                                        {userProfile.rejectionReason}
                                    </p>
                                </div>
                            )}
                            <p style={{ color: '#666', marginTop: '20px', fontSize: '14px', marginBottom: '20px' }}>
                                If you believe this was a mistake or would like to reapply, please contact support or create a new account.
                            </p>
                            <button
                                onClick={handleReapply}
                                style={{
                                    background: '#4CAF50',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '8px',
                                    padding: '12px 24px',
                                    fontSize: '16px',
                                    fontWeight: 'bold',
                                    cursor: 'pointer',
                                    width: '100%',
                                    marginTop: '10px',
                                    transition: 'background 0.3s'
                                }}
                                onMouseOver={(e) => e.target.style.background = '#45a049'}
                                onMouseOut={(e) => e.target.style.background = '#4CAF50'}
                            >
                                🔄 Reapply
                            </button>
                        </div>
                    ) : (
                        <div className="info-box">
                            <p>
                                Your account is currently under review by our admin team. 
                                This process typically takes 24-48 hours.
                            </p>
                            <p>
                                You will be automatically redirected to your dashboard once your account is approved.
                            </p>
                            <p>
                                You will then be able to start earning money by chatting with users!
                            </p>
                        </div>
                    )}
                    <div className="status-badge">
                        <span className="status-dot"></span>
                        <span>Status: {userProfile?.status || (checkingStatus ? 'checking...' : 'loading...')}</span>
                        {checkingStatus && <span className="checking-indicator">Checking...</span>}
                        {/* Debug info - remove later */}
                        {(process.env.NODE_ENV === 'development') && (
                            <div style={{ fontSize: '12px', marginTop: '10px', color: '#999' }}>
                                Debug: userProfile.status={userProfile?.status}, user.status={user?.status}, rejectionReason={userProfile?.rejectionReason || 'none'}
                            </div>
                        )}
                    </div>
                    <button 
                        className="exit-btn"
                        onClick={handleLogout}
                    >
                        Exit
                    </button>
                </div>
            </div>
        </div>
    );
}

export default WaitingApproval;
